import { useEffect, useId, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import {
  TextLessonEditor,
  type TextLessonEditorHandle,
} from "@/components/instructor/text-lesson-editor";
import { apiErrorText, readApiErrorResponse } from "@/lib/api-error";
import { browserTimeZone, isoToLocalInput, localInputToIso } from "@/lib/datetime-local";
import { apiFetch } from "@/services/http/api-fetch";

type Attachment = {
  id: string;
  fileName: string;
  mimeType: string | null;
  sizeBytes: number | null;
};

type NotesLocale = "en" | "hi";

const NOTES_LOCALES: readonly NotesLocale[] = ["en", "hi"];

/**
 * An untouched TipTap editor reports `<p></p>`, not "". Without this, merely
 * opening the Hindi tab on a lesson with no Hindi notes would save an empty
 * paragraph and mark the lesson as having notes.
 */
function normaliseNotes(html: string): string {
  return html.replace(/<p>\s*<\/p>/g, "").trim() ? html : "";
}

type Props = {
  courseId: string;
  moduleId: string;
  lessonId: string;
  lessonTitle: string;
  lessonSubtitle?: string | null;
  /** CRS-11: a live lesson can be turned into a video or text one (e.g. live classes are off). */
  contentType?: string;
  liveClasses?: boolean;
  onClose: () => void;
  onSaved: () => void;
};

export function LessonEditorPanel({
  courseId,
  moduleId,
  lessonId,
  lessonTitle,
  lessonSubtitle = null,
  contentType = "video",
  liveClasses = false,
  onClose,
  onSaved,
}: Props) {
  const t = useTranslations("instructor");
  const tRoot = useTranslations();
  const errorText = (err: unknown, fallbackKey: string) =>
    apiErrorText(err, tRoot, `instructor.${fallbackKey}`, { namespace: "instructor.errors" });
  const [title, setTitle] = useState(lessonTitle);
  const [subtitle, setSubtitle] = useState(lessonSubtitle ?? "");
  // Both locales share one editor; the tab decides which one it is showing.
  // `savedNotes` is what the server last held, so a save sends only the
  // locales the instructor actually changed and never clobbers the other.
  const [notes, setNotes] = useState<Record<NotesLocale, string>>({ en: "", hi: "" });
  const [savedNotes, setSavedNotes] = useState<Record<NotesLocale, string>>({ en: "", hi: "" });
  const [notesLocale, setNotesLocale] = useState<NotesLocale>("en");
  const tabRefs = useRef<Record<NotesLocale, HTMLButtonElement | null>>({ en: null, hi: null });
  const tabsId = useId();
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const replacePdfInput = useRef<HTMLInputElement>(null);
  const [replacingPdf, setReplacingPdf] = useState<Attachment | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const editorRef = useRef<TextLessonEditorHandle>(null);
  // F9: drip schedule — what the input shows (browser-local wall time) and what is stored.
  const [unlockInput, setUnlockInput] = useState("");
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [scheduleSaved, setScheduleSaved] = useState(false);
  const scheduleInputId = useId();
  const scheduleHintId = useId();
  const [convertTo, setConvertTo] = useState<"video" | "text">("text");
  const [converting, setConverting] = useState(false);
  const convertSelectId = useId();
  // W-27: names the study-material file input.
  const studyMaterialsHeadingId = useId();

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const res = await apiFetch(
          `/v1/instructor/courses/${courseId}/modules/${moduleId}/lessons/${lessonId}`,
        );
        if (!res.ok) {
          const err = await readApiErrorResponse(res);
          if (!cancelled) setError(errorText(err, "loadLessonFailed"));
          return;
        }
        const json = (await res.json()) as {
          data?: {
            lesson: {
              title?: string;
              subtitle?: string | null;
              bodyHtml: string | null;
              bodyHtmlHi?: string | null;
              unlockAt?: string | null;
            };
            attachments: Attachment[];
          };
        };
        if (!cancelled) {
          if (json.data?.lesson.title) setTitle(json.data.lesson.title);
          setSubtitle(json.data?.lesson.subtitle ?? "");
          const loaded = {
            en: json.data?.lesson.bodyHtml ?? "",
            hi: json.data?.lesson.bodyHtmlHi ?? "",
          };
          setNotes(loaded);
          setSavedNotes(loaded);
          setUnlockInput(isoToLocalInput(json.data?.lesson.unlockAt ?? null));
          setAttachments(json.data?.attachments ?? []);
        }
      } catch (err) {
        // W-28 sibling: a network failure is said, not swallowed.
        if (!cancelled) setError(errorText(err, "loadLessonFailed"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [courseId, moduleId, lessonId, t]);

  async function handleSave() {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const withEditor = {
        ...notes,
        [notesLocale]: editorRef.current?.getHtml() ?? notes[notesLocale],
      };
      const current = { en: normaliseNotes(withEditor.en), hi: normaliseNotes(withEditor.hi) };
      const res = await apiFetch(
        `/v1/instructor/courses/${courseId}/modules/${moduleId}/lessons/${lessonId}`,
        {
          method: "PATCH",
          body: JSON.stringify({
            title: title.trim(),
            subtitle: subtitle.trim() ? subtitle.trim() : null,
            ...(current.en !== savedNotes.en ? { bodyHtml: current.en } : {}),
            ...(current.hi !== savedNotes.hi ? { bodyHtmlHi: current.hi } : {}),
          }),
        },
      );
      if (!res.ok) {
        setError(errorText(await readApiErrorResponse(res), "saveNotesFailed"));
        return;
      }
      setNotes(current);
      setSavedNotes(current);
      setSaved(true);
      onSaved();
    } catch (err) {
      // W-28 sibling: the notes stay in the editor; say the save did not reach the server.
      setError(errorText(err, "saveNotesFailed"));
    } finally {
      setSaving(false);
    }
  }

  /** F9: store the unlock time as UTC (`null` clears it — the session opens at once). */
  async function saveSchedule(unlockAt: string | null) {
    setSavingSchedule(true);
    setScheduleSaved(false);
    setError(null);
    try {
      const res = await apiFetch(
        `/v1/instructor/courses/${courseId}/modules/${moduleId}/lessons/${lessonId}`,
        { method: "PATCH", body: JSON.stringify({ unlockAt }) },
      );
      if (!res.ok) {
        setError(errorText(await readApiErrorResponse(res), "unlockFailed"));
        return;
      }
      if (unlockAt === null) setUnlockInput("");
      setScheduleSaved(true);
      onSaved();
    } catch (err) {
      setError(errorText(err, "unlockFailed"));
    } finally {
      setSavingSchedule(false);
    }
  }

  const unlockIso = localInputToIso(unlockInput);
  const unlockInPast = unlockIso !== null && Date.parse(unlockIso) <= Date.now();

  /** CRS-11: change a live lesson's type; allowed whether or not live classes are set up. */
  async function handleConvert() {
    setConverting(true);
    setError(null);
    try {
      const res = await apiFetch(
        `/v1/instructor/courses/${courseId}/modules/${moduleId}/lessons/${lessonId}`,
        { method: "PATCH", body: JSON.stringify({ contentType: convertTo }) },
      );
      if (!res.ok) {
        setError(errorText(await readApiErrorResponse(res), "convertLessonFailed"));
        return;
      }
      onSaved();
    } catch (err) {
      setError(errorText(err, "convertLessonFailed"));
    } finally {
      setConverting(false);
    }
  }

  function selectNotesLocale(next: NotesLocale, focus = false) {
    // Keep what was typed in the outgoing locale before the editor swaps content.
    const html = editorRef.current?.getHtml();
    if (html !== undefined) setNotes((prev) => ({ ...prev, [notesLocale]: html }));
    setNotesLocale(next);
    if (focus) tabRefs.current[next]?.focus();
  }

  function handleTabKeyDown(e: React.KeyboardEvent<HTMLButtonElement>) {
    const index = NOTES_LOCALES.indexOf(notesLocale);
    const last = NOTES_LOCALES.length - 1;
    const target =
      e.key === "ArrowRight"
        ? index === last
          ? 0
          : index + 1
        : e.key === "ArrowLeft"
          ? index === 0
            ? last
            : index - 1
          : e.key === "Home"
            ? 0
            : e.key === "End"
              ? last
              : null;
    if (target === null) return;
    e.preventDefault();
    selectNotesLocale(NOTES_LOCALES[target]!, true);
  }

  async function handleUploadPdf(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await apiFetch(
        `/v1/instructor/courses/${courseId}/modules/${moduleId}/lessons/${lessonId}/attachments`,
        {
          method: "POST",
          body: form,
        },
      );
      if (!res.ok) {
        setError(errorText(await readApiErrorResponse(res), "uploadPdfFailed"));
        return;
      }
      const json = (await res.json()) as { data?: { attachment: Attachment } };
      if (json.data?.attachment) {
        setAttachments((prev) => [...prev, json.data!.attachment]);
      }
      onSaved();
    } catch (err) {
      // W-28: a request that never reached the server (network drop) is said too.
      setError(errorText(err, "uploadPdfFailed"));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  }

  /** H2: replace one study-material PDF (same entry; the old file is deleted). */
  function startReplacePdf(file: Attachment) {
    setReplacingPdf(file);
    replacePdfInput.current?.click();
  }

  async function handleReplacePdf(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    const target = replacingPdf;
    e.target.value = "";
    if (!file || !target) return;
    setDeletingId(target.id);
    setError(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await apiFetch(
        `/v1/instructor/courses/${courseId}/modules/${moduleId}/lessons/${lessonId}/attachments/${target.id}/replace`,
        { method: "POST", body: form },
      );
      if (!res.ok) {
        setError(errorText(await readApiErrorResponse(res), "replacePdfFailed"));
        return;
      }
      const json = (await res.json()) as { data: { attachment: Attachment } };
      setAttachments((prev) =>
        prev.map((row) => (row.id === target.id ? json.data.attachment : row)),
      );
      onSaved();
    } catch (err) {
      setError(errorText(err, "replacePdfFailed"));
    } finally {
      setDeletingId(null);
      setReplacingPdf(null);
    }
  }

  /** CRS-12: remove one study-material PDF. */
  async function handleDeletePdf(file: Attachment) {
    if (!window.confirm(t("deletePdfConfirm", { name: file.fileName }))) return;
    setDeletingId(file.id);
    setError(null);
    try {
      const res = await apiFetch(
        `/v1/instructor/courses/${courseId}/modules/${moduleId}/lessons/${lessonId}/attachments/${file.id}`,
        { method: "DELETE" },
      );
      if (!res.ok) {
        setError(errorText(await readApiErrorResponse(res), "deletePdfFailed"));
        return;
      }
      setAttachments((prev) => prev.filter((row) => row.id !== file.id));
      onSaved();
    } catch (err) {
      setError(errorText(err, "deletePdfFailed"));
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <section
      className="rounded-lg border border-[var(--accent-border)] bg-[var(--accent-soft)]/40 p-4/20"
      data-testid="lesson-editor-panel"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-medium">{t("editSession")}</h3>
          <p className="text-sm text-[var(--text-muted)]">{lessonTitle}</p>
        </div>
        <button type="button" className="text-sm underline" onClick={onClose}>
          {t("close")}
        </button>
      </div>

      {loading ? (
        <p className="mt-4 text-sm text-[var(--text-muted)]">{t("loading")}</p>
      ) : (
        <div className="mt-4 space-y-4">
          {contentType === "live_session" ? (
            <div
              className="rounded border border-[var(--border)] p-3 text-sm"
              data-testid="convert-lesson-type-section"
            >
              {liveClasses ? null : (
                <p className="mb-2" role="status" data-testid="live-lesson-not-configured">
                  {t("liveLessonNotConfigured")}
                </p>
              )}
              <label htmlFor={convertSelectId} className="font-medium">
                {t("convertLessonType")}
              </label>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <select
                  id={convertSelectId}
                  data-testid="convert-lesson-type"
                  value={convertTo}
                  onChange={(e) => setConvertTo(e.target.value as "video" | "text")}
                  className="rounded border border-[var(--border-strong)] bg-[var(--surface-1)] px-3 py-2 text-[var(--text-primary)]"
                >
                  <option value="video">{t("contentTypeVideo")}</option>
                  <option value="text">{t("contentTypeText")}</option>
                </select>
                <button
                  type="button"
                  data-testid="convert-lesson-submit"
                  disabled={converting}
                  onClick={() => void handleConvert()}
                  className="rounded border px-3 py-2 disabled:opacity-60 hover:bg-[var(--surface-3)]"
                >
                  {t("convertLessonSubmit")}
                </button>
              </div>
            </div>
          ) : null}
          <label className="block text-sm font-medium">
            {t("sessionLabel")}
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="mt-1 w-full rounded border border-[var(--border-strong)] px-3 py-2 text-sm border-[var(--border-strong)] bg-[var(--surface-1)] text-[var(--text-primary)]"
              data-testid="lesson-title-input"
            />
          </label>
          <label className="block text-sm font-medium">
            {t("sessionTopic")}
            <input
              value={subtitle}
              onChange={(e) => setSubtitle(e.target.value)}
              placeholder={t("sessionTopicPlaceholder")}
              className="mt-1 w-full rounded border border-[var(--border-strong)] px-3 py-2 text-sm border-[var(--border-strong)] bg-[var(--surface-1)] text-[var(--text-primary)]"
              data-testid="lesson-subtitle-input"
            />
          </label>
          <div>
            <div
              role="tablist"
              aria-label={t("notesLocaleLabel")}
              className="inline-flex rounded-md border border-[var(--border-strong)] p-0.5"
              data-testid="notes-locale-tabs"
            >
              {NOTES_LOCALES.map((locale) => {
                const selected = locale === notesLocale;
                return (
                  <button
                    key={locale}
                    ref={(el) => {
                      tabRefs.current[locale] = el;
                    }}
                    type="button"
                    role="tab"
                    id={`${tabsId}-tab-${locale}`}
                    aria-selected={selected}
                    aria-controls={`${tabsId}-panel`}
                    tabIndex={selected ? 0 : -1}
                    lang={locale}
                    data-testid={`notes-locale-${locale}`}
                    onClick={() => selectNotesLocale(locale)}
                    onKeyDown={handleTabKeyDown}
                    className={
                      selected
                        ? "rounded px-3 py-1 text-sm font-medium bg-[var(--ink-12)] text-[var(--ink-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-ring)]"
                        : "rounded px-3 py-1 text-sm text-[var(--text-muted)] hover:bg-[var(--surface-3)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-ring)]"
                    }
                  >
                    {locale === "en" ? t("notesLocaleEnglish") : t("notesLocaleHindi")}
                  </button>
                );
              })}
            </div>
            <div
              role="tabpanel"
              id={`${tabsId}-panel`}
              aria-labelledby={`${tabsId}-tab-${notesLocale}`}
              lang={notesLocale}
              className="mt-2"
            >
              <TextLessonEditor
                ref={editorRef}
                initialHtml={notes[notesLocale]}
                onChange={(html) => setNotes((prev) => ({ ...prev, [notesLocale]: html }))}
              />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              data-testid="save-text-lesson"
              disabled={saving}
              onClick={handleSave}
              className="rounded bg-[var(--ink-12)] px-4 py-2 text-sm text-[var(--ink-1)] disabled:opacity-60"
            >
              {saving ? t("saving") : t("saveNotes")}
            </button>
            {saved ? (
              <span className="text-sm text-green-700 dark:text-green-400">{t("saved")}</span>
            ) : null}
          </div>

          <fieldset
            className="rounded border border-[var(--border)] p-3"
            data-testid="unlock-schedule"
          >
            <legend className="px-1 text-sm font-medium">{t("scheduleTitle")}</legend>
            <label htmlFor={scheduleInputId} className="block text-sm">
              {t("unlockAt")}
            </label>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <input
                id={scheduleInputId}
                type="datetime-local"
                data-testid="unlock-at-input"
                value={unlockInput}
                aria-describedby={scheduleHintId}
                onChange={(e) => {
                  setUnlockInput(e.target.value);
                  setScheduleSaved(false);
                }}
                className="rounded border border-[var(--border-strong)] bg-[var(--surface-1)] px-3 py-2 text-sm text-[var(--text-primary)]"
              />
              <button
                type="button"
                data-testid="save-unlock-schedule"
                disabled={savingSchedule || unlockIso === null}
                onClick={() => void saveSchedule(unlockIso)}
                className="rounded border px-3 py-2 text-sm disabled:opacity-60 hover:bg-[var(--surface-3)]"
              >
                {t("saveSchedule")}
              </button>
              <button
                type="button"
                data-testid="clear-unlock-schedule"
                disabled={savingSchedule}
                onClick={() => void saveSchedule(null)}
                className="text-sm underline disabled:opacity-60"
              >
                {t("clear")}
              </button>
              {scheduleSaved ? (
                <span className="text-sm text-green-700 dark:text-green-400" role="status">
                  {t("scheduleSaved")}
                </span>
              ) : null}
            </div>
            <p
              id={scheduleHintId}
              className="mt-1 text-xs text-[var(--text-muted)]"
              data-testid="unlock-timezone-hint"
            >
              {unlockInput ? null : `${t("unlockNotSet")} `}
              {t("unlockTimezoneHint", { zone: browserTimeZone() })}
            </p>
            {unlockInPast ? (
              <p className="mt-1 text-xs text-[var(--warning)]" data-testid="unlock-in-past-hint">
                {t("unlockInPastHint")}
              </p>
            ) : null}
          </fieldset>

          <div data-testid="lesson-study-materials">
            <h4
              id={studyMaterialsHeadingId}
              className="text-sm font-medium"
              data-testid="lesson-study-materials-heading"
            >
              {t("studyMaterial")}
            </h4>
            <p className="mt-1 text-sm text-[var(--text-muted)]">{t("studyMaterialHint")}</p>
            <input
              type="file"
              accept="application/pdf,.pdf"
              data-testid="upload-pdf-attachment"
              aria-labelledby={studyMaterialsHeadingId}
              disabled={uploading}
              onChange={handleUploadPdf}
              className="mt-2 block text-sm"
            />
            <input
              ref={replacePdfInput}
              type="file"
              accept="application/pdf,.pdf"
              className="hidden"
              data-testid="replace-pdf-attachment-input"
              aria-hidden
              tabIndex={-1}
              onChange={handleReplacePdf}
            />
            {attachments.length > 0 ? (
              <ul className="mt-2 space-y-1 text-sm" data-testid="lesson-attachment-list">
                {attachments.map((file) => (
                  <li
                    key={file.id}
                    data-testid={`attachment-${file.id}`}
                    className="flex items-center gap-3"
                  >
                    <span>{file.fileName}</span>
                    <button
                      type="button"
                      className="text-sm underline disabled:opacity-60"
                      data-testid={`replace-attachment-${file.id}`}
                      disabled={deletingId !== null}
                      aria-label={t("replacePdfLabel", { name: file.fileName })}
                      onClick={() => startReplacePdf(file)}
                    >
                      {t("replacePdf")}
                    </button>
                    <button
                      type="button"
                      className="text-sm underline disabled:opacity-60"
                      data-testid={`delete-attachment-${file.id}`}
                      disabled={deletingId !== null}
                      aria-label={t("deletePdfLabel", { name: file.fileName })}
                      onClick={() => void handleDeletePdf(file)}
                    >
                      {t("deletePdf")}
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-2 text-sm text-[var(--text-muted)]">{t("noPdfs")}</p>
            )}
          </div>
        </div>
      )}

      {error ? (
        <p role="alert" className="mt-3 text-sm text-red-600" data-testid="lesson-editor-error">
          {error}
        </p>
      ) : null}
    </section>
  );
}
